import React, { useEffect, useState, useCallback } from "react";
import Moment from "react-moment";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../app/store/auth";
import { useDispatch, useSelector } from "react-redux";
import { getComplianceReport } from "../app/store/report";
import { useLoadComplianceReportQuery } from "../app/store/reportApiSlice";
import useDocDownload from "../app/hooks/useDocDownload";
import { getComponentUserStatus, getToolsCount, UpdateComponentsUserStatus } from "../app/store/tools";
import { setCurrentPageTitle, setCurrentRoute } from "../app/store/layoutUI";

const COMPLIANCE_STYLES = {
  pageWrap: {
    padding: "12px 0 4px 0"
  },
  shell: {
    borderRadius: "24px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 18px 38px rgba(15, 23, 42, 0.06)",
    overflow: "hidden"
  },
  hero: {
    padding: "24px 24px 18px 24px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flex: "1"
  },
  titleIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(79, 70, 229, 0.16) 100%)",
    color: "#4338ca",
    fontSize: "24px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)"
  },
  titleCopy: {
    minWidth: 0
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "10px"
  },
  title: {
    fontSize: "24px",
    fontWeight: 800,
    lineHeight: 1.15,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: "#475569",
    margin: "8px 0 0 0",
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#334155",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginTop: "20px"
  },
  statCard: {
    borderRadius: "18px",
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 10px 26px rgba(37, 99, 235, 0.06)"
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "6px"
  },
  statValue: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1
  },
  statHint: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#64748b",
    marginTop: "6px"
  },
  body: {
    padding: "20px 24px 24px 24px"
  },
  tableWrap: {
    borderRadius: "20px",
    overflow: "auto",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff"
  },
  table: {
    marginBottom: 0
  },
  tableHeadCell: {
    borderTop: "none",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "14px 16px"
  },
  cell: {
    verticalAlign: "middle",
    padding: "16px"
  },
  serialBadge: {
    minWidth: "34px",
    height: "34px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4338ca",
    fontSize: "13px",
    fontWeight: 800
  },
  dateText: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#0f172a"
  },
  yearBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "rgba(37, 99, 235, 0.08)",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: 800
  },
  notesText: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#475569",
    whiteSpace: "pre-wrap"
  },
  docsList: {
    display: "grid",
    gap: "8px"
  },
  docButton: {
    border: "none",
    background: "rgba(37, 99, 235, 0.08)",
    borderRadius: "12px",
    padding: "10px 12px",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 800,
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  emptyState: {
    padding: "42px 20px",
    textAlign: "center",
    background: "linear-gradient(180deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,1) 100%)"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    borderRadius: "20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4f46e5",
    fontSize: "28px",
    marginBottom: "14px"
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "8px"
  },
  emptyText: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#64748b",
    margin: "0 auto"
  }
};

const COMPLIANCE_RPT_ROUTE = [
  { name: "Reports", url: "" },
  { name: "Compliance Documents", url: "" }
];

const ComplianceRpt = () => {

  const [complianceData, setComplianceData] = useState([]);
  const [partnerCompliance, setPartnerCompliance] = useState(false);

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const complianceReportSelector = useSelector(getComplianceReport);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);
  const toolsCountSelector = useSelector(getToolsCount);

  const [handleDownloadFile] = useDocDownload();

  const usrType = localStorage.getItem("userType");

  const currentUser = getCurrentUser();
  const dispatch = useDispatch();

  const usrId = currentUser?.linkUser || currentUser?.id;
  const isCandidate = usrType === "candidate";

  // Determine target compliance report ID:
  // If candidate: use usrId.
  // If partner/recruiter: use userDetailsSelector?.partnerid.
  const complianceReportId = isCandidate ? usrId : userDetailsSelector?.partnerid;

  const { refetch: refetchComplianceReport } = useLoadComplianceReportQuery(
    complianceReportId,
    { skip: !complianceReportId }
  );

  useEffect(() => {
    dispatch(setCurrentPageTitle("Compliance Documents"));
    dispatch(setCurrentRoute(COMPLIANCE_RPT_ROUTE));

    let targetUsrId = currentUser?.linkUser || currentUser?.id;
    if (usrType !== "candidate") {
      if (!userDetailsSelector && targetUsrId)
        dispatch(loadUserDetails(targetUsrId));
      setPartnerCompliance(true);
    }
  }, [dispatch, usrType, currentUser, userDetailsSelector]);

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 25);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Compliance",
      performerId: currentUser.id,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 25
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }, [componentUserStatusSelector, currentUser?.id, dispatch]);

  useEffect(() => {
    if (complianceReportSelector.length > 0) {
      setComplianceData(complianceReportSelector);
      let compItems = "";
      complianceReportSelector.forEach((x, key) => {
        if (key > 0) compItems += ",";
        compItems += x.id;
      });

      const appObj = toolsCountSelector.filter(x => x.key === 0);
      let appCiid = 0;
      if (appObj.length > 0)
        appCiid = appObj[0].value;

      handleUpdateComponent(compItems, appCiid);
    } else {
      setComplianceData([]);
    }
  }, [complianceReportSelector, toolsCountSelector, handleUpdateComponent]);

  const handleRefresh = useCallback(() => {
    refetchComplianceReport();
  }, [refetchComplianceReport]);

  const totalReports = complianceData.length;
  const totalDocuments = complianceData.reduce((count, item) => count + (item.complianceDocs?.length || 0), 0);
  const coveredYears = new Set(complianceData.map((item) => item.year).filter(Boolean)).size;

  return (
    <React.Fragment>
      <div className="col-md-12" style={COMPLIANCE_STYLES.pageWrap}>
        <style>{`
          .compliance-rpt-table tbody tr:hover {
            background-color: #fbfdff;
          }
          .refresh-btn:hover {
            background-color: #f8fafc !important;
            border-color: #cbd5e1 !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }
          .refresh-btn:active {
            transform: translateY(0);
          }
          .doc-btn:hover {
            background: rgba(37, 99, 235, 0.14) !important;
            transform: translateX(4px);
          }
        `}</style>
        <div style={COMPLIANCE_STYLES.shell}>
          <div style={COMPLIANCE_STYLES.hero}>
            <div style={COMPLIANCE_STYLES.heroRow}>
              <div style={COMPLIANCE_STYLES.titleWrap}>
                <div style={COMPLIANCE_STYLES.titleIcon}>
                  <i className="fa fa-check-square-o" aria-hidden="true"></i>
                </div>
                <div style={COMPLIANCE_STYLES.titleCopy}>
                  <h1 style={COMPLIANCE_STYLES.title}>Compliance Documents</h1>
                  {/* <p style={COMPLIANCE_STYLES.subtitle}>
                    Review yearly compliance records, open supporting documents, and track how many files are available across your compliance history.
                  </p> */}
                </div>
              </div>
              <button
                className="refresh-btn"
                style={COMPLIANCE_STYLES.refreshButton}
                onClick={handleRefresh}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
                Refresh
              </button>
            </div>
            <div style={COMPLIANCE_STYLES.statsRow}>
              <div style={COMPLIANCE_STYLES.statCard}>
                <div style={COMPLIANCE_STYLES.statLabel}>Reports</div>
                <div style={COMPLIANCE_STYLES.statValue}>{totalReports}</div>
                <div style={COMPLIANCE_STYLES.statHint}>Compliance report entries currently available.</div>
              </div>
              <div style={COMPLIANCE_STYLES.statCard}>
                <div style={COMPLIANCE_STYLES.statLabel}>Documents</div>
                <div style={COMPLIANCE_STYLES.statValue}>{totalDocuments}</div>
                <div style={COMPLIANCE_STYLES.statHint}>Supporting files attached across all compliance records.</div>
              </div>
              <div style={COMPLIANCE_STYLES.statCard}>
                <div style={COMPLIANCE_STYLES.statLabel}>Years Covered</div>
                <div style={COMPLIANCE_STYLES.statValue}>{coveredYears}</div>
                <div style={COMPLIANCE_STYLES.statHint}>Distinct reporting years represented in this list.</div>
              </div>
            </div>
          </div>
          <div style={COMPLIANCE_STYLES.body}>
            <div style={COMPLIANCE_STYLES.tableWrap}>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0 compliance-rpt-table" style={COMPLIANCE_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}></th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Date & Time Added</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Year</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Notes</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Document Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complianceData.length > 0 ? complianceData.map((item, key) => (
                      <tr key={item.id || key}>
                        <td style={COMPLIANCE_STYLES.cell}>
                          <span style={COMPLIANCE_STYLES.serialBadge}>{key + 1}</span>
                        </td>
                        <td style={COMPLIANCE_STYLES.cell}>
                          <div style={COMPLIANCE_STYLES.dateText}>
                            <Moment format="MMM Do YYYY, h:mm a">
                              {item.startDateTime}
                            </Moment>
                          </div>
                        </td>
                        <td style={COMPLIANCE_STYLES.cell}>
                          <span style={COMPLIANCE_STYLES.yearBadge}>{item.year || "N/A"}</span>
                        </td>
                        <td style={COMPLIANCE_STYLES.cell}>
                          <div style={COMPLIANCE_STYLES.notesText}>{item.notes || "No notes added."}</div>
                        </td>
                        <td style={COMPLIANCE_STYLES.cell}>
                          <div style={COMPLIANCE_STYLES.docsList}>
                            {(item.complianceDocs || []).map((doc, docIndex) => (
                              <button
                                key={doc.id || `${item.id || key}-${docIndex}`}
                                className="doc-btn"
                                style={COMPLIANCE_STYLES.docButton}
                                onClick={() => handleDownloadFile(doc)}
                              >
                                {doc.documentName}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ ...COMPLIANCE_STYLES.cell, padding: 0 }}>
                          <div style={COMPLIANCE_STYLES.emptyState}>
                            <div style={COMPLIANCE_STYLES.emptyIcon}>
                              <i className="fa fa-check-square-o" aria-hidden="true"></i>
                            </div>
                            <div style={COMPLIANCE_STYLES.emptyTitle}>No compliance documents found</div>
                            <div style={COMPLIANCE_STYLES.emptyText}>
                              Compliance records will appear here once reports and supporting files are available for your account or partner.
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ComplianceRpt;
